import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Landmark,
  Send,
  BarChart3,
  Zap,
  ArrowRight,
  Building2,
  Wallet,
  CreditCard,
  Users,
  FileText,
} from "lucide-react";
import bankingImg from "../Accets/bankingapi.jpg";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as the rest of the site.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const features = [
  { icon: Landmark, title: "Account Verification", desc: "Instant account validation before transfers." },
  { icon: Send, title: "Fund Transfers", desc: "NEFT, IMPS and RTGS APIs." },
  { icon: BarChart3, title: "Balance Check", desc: "Real-time account information." },
  { icon: Zap, title: "Fast Integration", desc: "REST APIs with developer SDKs." },
];

const apiList = [
  { title: "Account Verification", icon: Landmark },
  { title: "Balance Enquiry", icon: BarChart3 },
  { title: "Payout APIs", icon: Wallet },
  { title: "Fund Transfer", icon: Send },
  { title: "Beneficiary Management", icon: Users },
  { title: "Statement APIs", icon: FileText },
];

const faqs = [
  {
    question: "What are Banking APIs?",
    answer:
      "Banking APIs allow businesses to securely connect with banks for payments, account verification, balance checks and fund transfers.",
  },
  {
    question: "Are the APIs secure?",
    answer:
      "Yes. All APIs use OAuth authentication, encryption and comply with industry security standards.",
  },
  {
    question: "How quickly can I integrate?",
    answer:
      "Most businesses complete integration within a day using our SDKs and documentation.",
  },
];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "transaction flow" motif, distinct from every
// other page's hero animation. Horizontal lanes carry traveling pulse
// dashes like transactions moving through a ledger, converging toward
// a central node cluster with a soft breathing glow behind it.
// ---------------------------------------------------------------------
const lanes = [
  { top: "16%", duration: 6, delay: 0, gold: false },
  { top: "30%", duration: 8, delay: 1.2, gold: true },
  { top: "44%", duration: 5.5, delay: 2.4, gold: false },
  { top: "58%", duration: 7, delay: 0.6, gold: false },
  { top: "72%", duration: 9, delay: 1.8, gold: true },
  { top: "86%", duration: 6.5, delay: 3, gold: false },
];

function TransactionField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="txn-glow" />

      {lanes.map((lane, i) => (
        <span key={i} className="txn-lane" style={{ top: lane.top }}>
          <span
            className="txn-pulse"
            style={{
              backgroundColor: lane.gold ? theme.gold : theme.accent,
              boxShadow: `0 0 10px 2px ${lane.gold ? theme.gold : theme.accent}77`,
              animationDuration: `${lane.duration}s`,
              animationDelay: `${lane.delay}s`,
            }}
          />
        </span>
      ))}

      <span className="txn-node-cluster">
        <span className="txn-node-ring" />
        <span className="txn-node-ring txn-node-ring-2" />
        <span className="txn-node-core" />
      </span>
    </div>
  );
}

export default function BankingApis() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .txn-glow {
          position: absolute; top: 50%; left: 68%; width: 32rem; height: 32rem;
          margin: -16rem 0 0 -16rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}30, transparent 68%);
          filter: blur(70px);
          animation: txnGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes txnGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }

        .txn-lane {
          position: absolute; left: 0; right: 0; height: 1px;
          background: rgba(255,255,255,0.06);
        }
        .txn-pulse {
          position: absolute; top: -2px; left: -6%; width: 20px; height: 5px;
          border-radius: 9999px;
          animation-name: txnPulseTravel;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        @keyframes txnPulseTravel {
          0% { transform: translateX(0); opacity: 0; }
          8% { opacity: 1; }
          68% { left: 68%; opacity: 1; }
          78% { opacity: 0; }
          100% { left: 68%; opacity: 0; }
        }

        .txn-node-cluster {
          position: absolute; top: 50%; left: 68%; width: 6px; height: 6px;
          margin: -3px 0 0 -3px;
        }
        .txn-node-core {
          position: absolute; inset: 0; border-radius: 9999px;
          background: ${theme.accent};
          box-shadow: 0 0 16px 4px ${theme.accent}aa;
        }
        .txn-node-ring {
          position: absolute; top: 50%; left: 50%; width: 4px; height: 4px;
          border-radius: 9999px; border: 1px solid ${theme.accent};
          animation: txnRingPing 3.5s ease-out infinite;
        }
        .txn-node-ring-2 { border-color: ${theme.gold}; animation-delay: 1.2s; }
        @keyframes txnRingPing {
          0% { width: 4px; height: 4px; opacity: 0.8; transform: translate(-50%, -50%); }
          100% { width: 14rem; height: 14rem; opacity: 0; transform: translate(-50%, -50%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .txn-glow, .txn-pulse, .txn-node-ring, .txn-node-ring-2 { animation: none !important; }
        }

        .card-glow-light {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow-light { opacity: 0.18; }
      `}</style>

      {/* ================= HERO (dark, transaction field bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <TransactionField />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow light>Banking APIs</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-white">
              <SplitText text="Connect directly with" />
              <br />
              <span style={{ color: theme.accent }}>
                <SplitText text="banking infrastructure" delay={0.25} />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="text-white/60 mt-8 text-lg leading-8 max-w-xl"
            >
              Access account verification, balance enquiry, fund transfers,
              payouts and banking services through powerful APIs.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Get API Access
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                
              <a    href="#available-apis"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-4 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  View APIs
                </a>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-[32px] blur-[100px] opacity-20" style={{ backgroundColor: theme.accent }} />
            <img src={bankingImg} alt="Banking APIs" className="relative rounded-[32px] border border-white/10 shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* ================= FEATURES (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Why Banking APIs</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Why choose <span style={{ color: theme.accent }}>Banking APIs?</span>
            </h2>
            <p className="mt-4" style={{ color: theme.inkMuted }}>
              Enterprise-grade banking infrastructure for modern businesses.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((item) => (
              <motion.div
                key={item.title}
                variants={staggerItem}
                whileHover={{ y: -8 }}
                className="group relative rounded-3xl p-8 overflow-hidden"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <span className="card-glow-light" />
                <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}>
                  <item.icon size={26} />
                </div>
                <h3 className="relative text-2xl font-semibold" style={{ color: theme.ink }}>{item.title}</h3>
                <p className="relative mt-4" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= AVAILABLE APIS (dark) ================= */}
      <section id="available-apis" className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <RevealSection className="text-center mb-16">
            <Eyebrow center light>API Suite</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Available <span style={{ color: theme.accent }}>APIs</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-3 gap-8"
          >
            {apiList.map((api) => (
              <motion.div
                key={api.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="rounded-2xl py-10 text-center flex flex-col items-center gap-4 transition-colors"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}>
                  <api.icon size={26} />
                </div>
                <h3 className="text-xl font-semibold text-white">{api.title}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FAQ (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-5xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>FAQs</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Frequently Asked <span style={{ color: theme.accent }}>Questions</span>
            </h2>
          </RevealSection>

          <div>
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="py-6 cursor-pointer"
                style={{ borderBottom: `1px solid ${theme.border}` }}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold" style={{ color: theme.ink }}>{faq.question}</h3>
                  <span
                    className="text-2xl font-light transition-transform"
                    style={{ color: theme.accent, transform: openFAQ === index ? "rotate(45deg)" : "rotate(0deg)" }}
                  >
                    +
                  </span>
                </div>

                {openFAQ === index && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 leading-7"
                    style={{ color: theme.inkMuted }}
                  >
                    {faq.answer}
                  </motion.p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight text-white">
            <SplitText text="Ready to" />
            <span className="block">
              <SplitText text="integrate?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Connect your business to modern banking infrastructure today.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Sales
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}